import {
    useFileStatisticsTableColumns,
    useGraderProgressTableColumns,
    useStudentStatisticsTableColumns,
} from 'components/GradingInsights/Table';
import { useApiTableColumns, useSubmissionsTableColumns } from 'components/Submissions/Table';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import {
    calculateClassPerformanceChartData,
    calculateClassPerformanceStats,
    calculateDetailedGradingData,
    calculateGraderStats,
    calculateStudentLevelStats,
    calculateTimeSavedStats,
    getMultiselectFilterValue,
} from 'pages/Submissions/gradingInsights';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import { Stack, Text, useTableSortableHeaderCellEffectHandlers } from 'shell/components';
import { FilterOption, Placeholder, SortOrder, SubmissionTabs } from 'utils/constants';
import {
    compareCanvasRubrics,
    compareD2lRubrics,
    exportGraderStatisticsToCSV,
    exportGradingStatisticsToCSV,
    exportSubmissionsToCSV,
    getLatestConfirmedId,
    getUserPreferredFullName,
} from 'utils/helper';

const PageContext = createContext();

const handleFilters = (allData, filters, setSortedFilteredData) => {
    let res = [...allData];

    res = res.filter((submission) => {
        const searchCond =
            submission.files.some(({ name }) => {
                return name.toLowerCase().includes(filters.search.toLowerCase());
            }) ||
            submission.api?.user?.name?.toLowerCase()?.includes(filters.search.toLowerCase()) ||
            submission.api?.user?.id?.toLowerCase()?.includes(filters.search.toLowerCase());
        const statusCond = !filters.status.length
            ? true
            : filters.status.some((status) => {
                  if (status === 'ready') {
                      return submission.ai && !submission.grading;
                  }
                  if (status === 'reviewed') {
                      return submission.ai && submission.grading;
                  }
                  return false;
              });
        return searchCond && statusCond;
    });

    setSortedFilteredData(res);
};

const PageProvider = (props) => {
    const { course, accountUsers, staffAccountUsers, currentAssignment, submissions, setSubmissions, setBatch } =
        useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');

    const graderColumns = useGraderProgressTableColumns();
    const studentStatisticsColumns = useStudentStatisticsTableColumns();
    const fileStatisticsColumns = useFileStatisticsTableColumns();

    const submissionColumns = useSubmissionsTableColumns();
    const apiColumns = useApiTableColumns(submissions);
    const columns = currentAssignment?.api_assignment_id ? apiColumns : submissionColumns;

    const [confirmPushGradesDialog, setConfirmPushGradesDialog] = useState({ open: false });

    const [filters, setFilters] = useState({
        open: false,
        status: [],
        search: '',
    });
    const [activeTab, setActiveTab] = useState(SubmissionTabs.SUBMISSIONS);

    // Grading Progress States
    const [graderSortState, setGraderSortState] = useState({ order: SortOrder.DESC, orderBy: graderColumns[1].id });
    const [predictedGradingTime, setPredictedGradingTime] = useState('');
    const [calculatedTimeSavedPerSub, setCalculatedTimeSavedPerSub] = useState(Placeholder.NO_DATA);
    const [totalTimeSaved, setTotalTimeSaved] = useState(Placeholder.NO_DATA);

    const gradingStatisticsColumns = currentAssignment?.api_assignment_id
        ? studentStatisticsColumns
        : fileStatisticsColumns;

    // Student Performance States
    const [performanceFilters, setPerformanceFilters] = useState({
        criterion: FilterOption.ALL,
        grader: FilterOption.ALL,
    });

    // Student Statistics States
    const [isTableExpanded, setIsTableExpanded] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(() =>
        gradingStatisticsColumns.filter((col) => col.key !== 'name').map((col) => col.key)
    );
    const [studentSortState, setStudentSortState] = useState({
        order: SortOrder.ASC,
        orderBy: gradingStatisticsColumns[0].id,
    });
    const [statisticsFilters, setStatisticsFilters] = useState({
        criterion: FilterOption.ALL,
        grader: FilterOption.ALL,
    });

    const extras = useMemo(
        () => ({
            assignment: currentAssignment,
            accountUsers,
        }),
        [currentAssignment, accountUsers]
    );

    const submissionsData = useMemo(() => submissions || [], [submissions]);

    const {
        sortedFilteredData: sortedFilteredSubmissions,
        sortState,
        handleSetSortState,
    } = useTableSortableHeaderCellEffectHandlers(
        submissionsData,
        columns,
        filters,
        setSubmissions,
        handleFilters,
        extras
    );

    const resetFilters = useCallback(() => {
        setFilters((current) => ({
            ...current,
            status: [],
        }));
        handleSetSortState({ order: 'asc', orderBy: null });
    }, [handleSetSortState]);

    const resetSubmissions = useCallback(() => {
        setDialog({
            open: true,
            message: t('dialog.erase-assignment-data.title', 'Erase Existing Assignment Data?'),
            description: t(
                'dialog.erase-assignment-data.description',
                'If you reset, any existing submissions and associated grades/feedback will be permanently deleted.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                setSubmissions([]);
                setBatch([]);
                closeDialog();
                await Promise.all([
                    await assignmentService.deleteAssignmentSubmissions(currentAssignment.id),
                    await assignmentService.deleteAssignmentBatch(currentAssignment.id),
                ]);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    }, [closeDialog, currentAssignment?.id, setBatch, setSubmissions, setDialog, t]);

    const pushGrades = useCallback(async () => {
        const course_id = auth.course_id;
        const accessToken = localStorage.getItem('access_token');
        const APIAssignment = await apiService.getAssignment({
            course_id,
            accessToken,
            assignment_id: currentAssignment.api_assignment_id,
        });
        const is_matching =
            process.env.REACT_APP_KIND === 'CANVAS'
                ? compareCanvasRubrics(currentAssignment.rubric, APIAssignment.rubric)
                : compareD2lRubrics(currentAssignment.rubric, APIAssignment.Assessment?.Rubrics?.[0]?.CriteriaGroups);
        if (!is_matching) {
            setDialog({
                open: true,
                message: t('dialog.rubrics-not-synced.title', 'Rubrics Are Not Synced'),
                description: (
                    <Stack spacing={4}>
                        <Text>
                            {t(
                                'dialog.rubrics-not-synced.description',
                                "The rubric on your LMS doesn't match the one used to generate grades/feedback on TimelyGrader. If you continue, only the overall final grade will be passed back for each student."
                            )}
                        </Text>

                        <Text>
                            {t(
                                'dialog.rubrics-not-synced.description-cont',
                                'To pass back the graded rubric, please cancel and resync the assignment. Note that you will need to regrade the submissions.'
                            )}
                        </Text>
                    </Stack>
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.continue', 'Continue'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    setConfirmPushGradesDialog({ open: true, is_not_matching: true });
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            setConfirmPushGradesDialog({ open: true, is_not_matching: false });
        }
    }, [auth.course_id, closeDialog, currentAssignment?.api_assignment_id, currentAssignment?.rubric, setDialog, t]);

    const handleCalculateTimeSave = useCallback(() => {
        const { savedPerSub, totalSaved } = calculateTimeSavedStats(submissions, predictedGradingTime);
        setCalculatedTimeSavedPerSub(savedPerSub);
        setTotalTimeSaved(totalSaved);
    }, [submissions, predictedGradingTime]);

    const handleResetTimeSave = useCallback(() => {
        setPredictedGradingTime('');
        setCalculatedTimeSavedPerSub(Placeholder.NO_DATA);
        setTotalTimeSaved(Placeholder.NO_DATA);
    }, []);

    const handleColumnToggle = useCallback((columnKey) => {
        setVisibleColumns((prev) => {
            if (prev.includes(columnKey) && prev.length === 1) return prev;
            return prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey];
        });
    }, []);

    const handleSetGraderSortState = useCallback((columnId) => {
        setGraderSortState((prev) => ({
            orderBy: columnId,
            order: prev.orderBy === columnId && prev.order === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC,
        }));
    }, []);

    const handleSetStudentSortState = useCallback((columnId) => {
        setStudentSortState((prev) => ({
            orderBy: columnId,
            order: prev.orderBy === columnId && prev.order === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC,
        }));
    }, []);

    const handleStatisticsGraderFilter = useCallback((clickedOption) => {
        setStatisticsFilters((prev) => ({
            ...prev,
            grader: getMultiselectFilterValue(prev.grader, clickedOption),
        }));
    }, []);

    const handlePerformanceGraderFilter = useCallback((clickedOption) => {
        setPerformanceFilters((prev) => ({
            ...prev,
            grader: getMultiselectFilterValue(prev.grader, clickedOption),
        }));
    }, []);

    const staffGradersList = useMemo(() => {
        return (
            staffAccountUsers?.map((user) => ({
                id: user.owner_account_id,
                name: getUserPreferredFullName(user),
            })) || []
        );
    }, [staffAccountUsers]);

    const criteriaList = useMemo(() => {
        return (
            currentAssignment?.rubric?.criteria?.map((criterion) => ({
                id: criterion.id,
                title: criterion.title,
            })) || []
        );
    }, [currentAssignment]);

    const performanceFilterOptions = useMemo(
        () => ({
            criteria: [{ id: FilterOption.ALL, title: t('submission.overall-scores-label') }, ...criteriaList],
            graders: [{ id: FilterOption.ALL, name: t('submission.all-graders-label') }, ...staffGradersList],
        }),
        [criteriaList, staffGradersList, t]
    );

    const filteredClassSubmissions = useMemo(() => {
        if (!submissions) return [];

        let res = submissions.filter((s) => s.grading);

        if (performanceFilters.grader !== FilterOption.ALL) {
            const activeGraderId = performanceFilters.grader;
            if (Array.isArray(activeGraderId)) {
                res = res.filter((s) => activeGraderId.includes(getLatestConfirmedId(s.sessions)));
            } else {
                res = res.filter((s) => getLatestConfirmedId(s.sessions) === activeGraderId);
            }
        }
        return res;
    }, [submissions, performanceFilters.grader]);

    const gradingProgress = useMemo(() => {
        const total = submissions?.length || 0;
        const graded = submissions?.filter((s) => s.grading).length || 0;
        return {
            total,
            graded,
            percentage: total > 0 ? Math.round((graded / total) * 100) : 0,
        };
    }, [submissions]);

    const classPerformanceStats = useMemo(
        () => calculateClassPerformanceStats(filteredClassSubmissions, currentAssignment, performanceFilters),
        [filteredClassSubmissions, performanceFilters, currentAssignment]
    );

    const classPerformanceChartData = useMemo(
        () =>
            calculateClassPerformanceChartData(
                submissions || [],
                filteredClassSubmissions,
                currentAssignment,
                performanceFilters,
                staffGradersList
            ),
        [submissions, filteredClassSubmissions, currentAssignment, performanceFilters, staffGradersList]
    );

    const detailedGradingData = useMemo(
        () => calculateDetailedGradingData(submissions || [], currentAssignment, accountUsers),
        [submissions, currentAssignment, accountUsers]
    );

    const studentGradingStatistics = useMemo(
        () => calculateStudentLevelStats(detailedGradingData, statisticsFilters, currentAssignment),
        [detailedGradingData, statisticsFilters, currentAssignment]
    );

    const calculatedGraders = useMemo(
        () => calculateGraderStats(staffAccountUsers, submissions || [], currentAssignment),
        [staffAccountUsers, submissions, currentAssignment]
    );

    const sortedGraders = useMemo(() => {
        const { orderBy, order } = graderSortState;
        if (!orderBy) return calculatedGraders;

        const column = graderColumns.find((col) => col.id === orderBy);
        return column?.comparator ? column.comparator(order, calculatedGraders) : calculatedGraders;
    }, [calculatedGraders, graderSortState, graderColumns]);

    const sortedStudentGradingStatistics = useMemo(() => {
        const { orderBy, order } = studentSortState;
        if (!orderBy) return studentGradingStatistics;

        const column = gradingStatisticsColumns.find((col) => col.id === orderBy || col.key === orderBy);
        return column?.comparator ? column.comparator(order, studentGradingStatistics) : studentGradingStatistics;
    }, [studentGradingStatistics, studentSortState, gradingStatisticsColumns]);

    const exportSubmissions = useCallback(() => {
        return exportSubmissionsToCSV(course, currentAssignment, submissions, accountUsers, t);
    }, [course, currentAssignment, submissions, accountUsers, t]);

    const exportStatistics = useCallback(() => {
        return exportGradingStatisticsToCSV(currentAssignment, detailedGradingData, t);
    }, [currentAssignment, detailedGradingData, t]);

    const exportGraderStatistics = useCallback(() => {
        return exportGraderStatisticsToCSV(currentAssignment, calculatedGraders, gradingProgress.total, t);
    }, [currentAssignment, calculatedGraders, gradingProgress.total, t]);

    const gradingInsightsData = useMemo(
        () => ({
            // Grading Progress & Time
            gradingProgress,
            predictedGradingTime,
            setPredictedGradingTime,
            calculatedTimeSavedPerSub,
            totalTimeSaved,
            handleCalculateTimeSave,
            handleResetTimeSave,
            sortedGraders,
            graderSortState,
            handleSetGraderSortState,
            exportGraderStatistics,
            graderColumns,

            // Performance Charts
            classPerformanceStats,
            classPerformanceChartData,
            performanceFilters,
            setPerformanceFilters,
            performanceFilterOptions,
            handlePerformanceGraderFilter,

            // Student Statistics
            studentGradingStatistics: sortedStudentGradingStatistics,
            studentSortState,
            handleSetStudentSortState,
            statisticsFilters,
            setStatisticsFilters,
            handleStatisticsGraderFilter,
            exportStatistics,
            visibleColumns,
            onColumnToggle: handleColumnToggle,
            isTableExpanded,
            onToggleTableExpanded: setIsTableExpanded,
            gradingStatisticsColumns,
        }),
        [
            gradingProgress,
            predictedGradingTime,
            calculatedTimeSavedPerSub,
            totalTimeSaved,
            handleCalculateTimeSave,
            handleResetTimeSave,
            classPerformanceStats,
            classPerformanceChartData,
            performanceFilters,
            performanceFilterOptions,
            handlePerformanceGraderFilter,
            sortedGraders,
            graderSortState,
            handleSetGraderSortState,
            exportGraderStatistics,
            sortedStudentGradingStatistics,
            studentSortState,
            handleSetStudentSortState,
            statisticsFilters,
            handleStatisticsGraderFilter,
            exportStatistics,
            visibleColumns,
            handleColumnToggle,
            isTableExpanded,
            graderColumns,
            gradingStatisticsColumns,
        ]
    );

    const contextValue = useMemo(
        () => ({
            assignment: currentAssignment,
            submissions,
            setSubmissions,
            confirmPushGradesDialog,
            setConfirmPushGradesDialog,
            sortedFilteredSubmissions,
            sortState,
            handleSetSortState,
            filters,
            setFilters,
            resetFilters,
            columns,
            activeTab,
            setActiveTab,
            gradingInsightsData,
            exportSubmissions,
            resetSubmissions,
            pushGrades,
        }),
        [
            currentAssignment,
            submissions,
            setSubmissions,
            confirmPushGradesDialog,
            setConfirmPushGradesDialog,
            sortedFilteredSubmissions,
            sortState,
            handleSetSortState,
            filters,
            setFilters,
            resetFilters,
            columns,
            activeTab,
            setActiveTab,
            gradingInsightsData,
            exportSubmissions,
            resetSubmissions,
            pushGrades,
        ]
    );
    return <PageContext.Provider value={contextValue}>{props.children}</PageContext.Provider>;
};

export { PageContext };
export default PageProvider;
